/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import _ from "lodash"
import moment from "moment"
import {
  ActivityIndicator,
  Appearance,
  ListRenderItem,
  ListRenderItemInfo,
  SectionListData,
  TouchableOpacity,
  useWindowDimensions,
  ViewStyle,
  ImageStyle
} from "react-native"
import {
  Cell,
  FloatButton,
} from "../cycle-water-quality-multi-measurement-list/cycle-water-quality-multi-measurement-list-screen"
import { Divider, Icon, Modal, ScrollView, Skeleton, useDisclose, View } from "native-base"
import { Button, JalaIcon, JalaLoading, LazyRender, ListEmpty, Screen, SectionList, Text, AutoImage } from "../../../../components"
import { SafeAreaView } from "react-native-safe-area-context"
import { color, nativeBaseTheme, images } from "../../../../theme"
import { navigate } from "../../../../navigators"
import { observer } from "mobx-react-lite"
import { translate } from "../../../../i18n"
import { useStores } from "../../../../models"
import { delay } from "../../../../utils/delay"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  flex: 1,
  flexGrow: 1
}

const CONTENT_CONTAINER: ViewStyle = {
  // flexGrow: 1,
  paddingBottom: 68
}

const SCROLL_VIEW_DETAIL_ITEM: ViewStyle = {
  flex: 1,
  marginBottom: 8
}

const BUTTON_DETAIL_ITEM: ViewStyle = {
  width: 120,
  alignSelf: "center",
  borderRadius: 40
}

const EMPTY_NOTES: ImageStyle = {
  resizeMode: 'contain',
  width: 14,
  height: 12,
  alignSelf: 'center'
}

const TableHead: FC<{ colorScheme?: "light" | "dark" }> = observer(function TableHead(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;

  const renderPlaceholder = useCallback(() => {
    return <Skeleton.Text lines={1} paddingX="8px" paddingY="10px" />
  }, [])

  return <>
    <View
      borderWidth="1px"
      borderColor={`gray.${colorScheme === 'light' ? '200' : '800'}`}
      backgroundColor={`gray.${colorScheme === 'light' ? '100' : '900'}`}
      flexDirection="row"
    >
      <LazyRender renderPlaceholder={renderPlaceholder}>
        <Cell text={_.upperFirst(translate('feedingTrayControls.logged_time'))} width="64px" textPreset="body3" colorScheme={colorScheme} />
        <Cell text={`${_.upperFirst(translate('feedingTrayControls.tray'))} 1`} textPreset="body3" textProps={{ ellipsizeMode: "head" }} colorScheme={colorScheme} />
        <Cell text={`${_.upperFirst(translate('feedingTrayControls.tray'))} 2`} textPreset="body3" textProps={{ ellipsizeMode: "head" }} colorScheme={colorScheme} />
        <Cell text={`${_.upperFirst(translate('feedingTrayControls.tray'))} 3`} textPreset="body3" textProps={{ ellipsizeMode: "head" }} colorScheme={colorScheme} />
        <Cell text={`${_.upperFirst(translate('feedingTrayControls.tray'))} 4`} textPreset="body3" textProps={{ ellipsizeMode: "head" }} colorScheme={colorScheme} />
        <Cell text={_.upperFirst(translate('feedingTrayControls.remark'))} width="64px" textPreset="body3" colorScheme={colorScheme} />
        <Cell text={_.upperFirst(translate('edit'))} width="48px" padding="0px" alignItems="center" justifyContent="center" textPreset="body3" colorScheme={colorScheme} />
      </LazyRender>
    </View>
    <View paddingY="2px" paddingX="16px" justifyContent="center">
      <Text>
        <Text tx="feedingTrayControls.empty" preset="body3" transformText={_.startCase} colorScheme={colorScheme} color={color.primary} bold/>
        <Text text=": <10%, " preset="body3" colorScheme={colorScheme}/>
        <Text tx="feedingTrayControls.few" preset="body3" transformText={_.startCase} colorScheme={colorScheme} color={color.palette.orange500} bold/>
        <Text text=": 10% - 50%, " preset="body3" colorScheme={colorScheme}/>
        <Text tx="feedingTrayControls.plenty" preset="body3" transformText={_.startCase} colorScheme={colorScheme} color={color.palette.red700} bold/>
        <Text text=": >50%" preset="body3" colorScheme={colorScheme}/>
      </Text>
    </View>
  </>;
});

const SectionHeader: FC<{ colorScheme?: "light" | "dark", info: { section: SectionListData<any, any> } }> = observer(function SectionHeader(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info } = props;
  const { section: { title: { age, feed_at } } } = info;

  const renderPlaceholder = useCallback(() => {
    return <View height="40px"><Skeleton.Text width="30%" lines={1} /></View>
  }, [])

  return <View height="40px" backgroundColor={colorScheme === "light" ? color.palette.white : nativeBaseTheme.colors.dark["50"]}>
    <View padding="8px">
      <LazyRender renderPlaceholder={renderPlaceholder}>
        <Text
          text={`(${age}) ${moment(feed_at).format('dddd, DD MMM YYYY')}`}
          preset="title4"
          colorScheme={colorScheme}
          color={`muted.${colorScheme === 'light' ? '700' : '300'}`}
        />
      </LazyRender>
    </View>
    <Divider />
  </View>
})

const DetailItemModal: FC<{ colorScheme?: "light" | "dark", item: any }> = observer(function DetailItemModal(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), item = {}, children } = props;
  const { isOpen, onClose, onOpen } = useDisclose()
  const { age, logged_at, feeding_tray_controls } = item
  const { remark } = feeding_tray_controls.find((f) => f.remark) || {}

  return (<>
    <TouchableOpacity onPress={onOpen}>
      {children}
    </TouchableOpacity>
    <Modal isOpen={isOpen} onClose={onClose}>
      <View
        flex={1}
        flexGrow={1}
        alignSelf="stretch"
        marginY="80px"
        marginX="16px"
        padding="16px"
        rounded="8px"
        backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
      >
        <Text tx="feeds.remark" transformText={_.upperFirst} preset="display2" colorScheme={colorScheme} marginBottom="8px" />
        <Text
          text={`(${age}) ${moment.utc(logged_at).local().format('dddd, DD MMM YYYY HH:mm')}`}
          preset="body2"
          colorScheme={colorScheme}
          marginBottom="8px"
        />
        <ScrollView
          contentContainerStyle={SCROLL_VIEW_DETAIL_ITEM}
        >
          <Text text={remark || '-'} preset="body2" colorScheme={colorScheme} />
        </ScrollView>
        <Button
          testID="jala:id/cancel-button"
          tx="common.ok"
          preset="primary"
          colorScheme={colorScheme}
          style={BUTTON_DETAIL_ITEM}
          onPress={onClose}
        />
      </View>
    </Modal>
  </>)
})

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any>, handleEdit: (item: any) => void }> = observer(function Item(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info, handleEdit } = props;
  const {
    item,
    item: {
      logged_at,
      feeding_tray_controls,
      // quantity, accumulation,
      _pushing,
      synced
    }
  } = info;

  const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });
  const loggedAt = moment.utc(logged_at).local();
  const isRemarkExist = feeding_tray_controls.find((f) => f.remark) || false


  const renderPlaceholder = useCallback(() => {
    return <View height="40px">
      <Skeleton.Text lines={1} padding="8px" _line={{ height: '14px' }} />
    </View>
  }, [])

  const onPressEdit = useCallback(() => handleEdit(item), [handleEdit, item])

  const feedingTrayControl = {};
  feeding_tray_controls.forEach((f) => {
    feedingTrayControl[f.tray_number] = f.feed_remaining_percent
  })

  const valueFormatted = (value) => {
    return {
      0: translate('feedingTrayControls.empty_short'),
      33: translate('feedingTrayControls.few_short'),
      66: translate('feedingTrayControls.plenty_short'),
    }[value]
  }

  const textColor = (value) => ({
    0: color.primary,
    33: color.palette.orange,
    66: color.palette.red500,
  }[value])

  return <LazyRender renderPlaceholder={renderPlaceholder}>
     <View height="40px" flexDirection="row" position="relative">
       <Cell text={loggedAt.format('HH:mm')} width="64px" colorScheme={colorScheme} />
       <Cell text={valueFormatted(feedingTrayControl[1])} colorScheme={colorScheme} textColor={textColor(feedingTrayControl[1])} />
       <Cell text={valueFormatted(feedingTrayControl[2])} colorScheme={colorScheme} textColor={textColor(feedingTrayControl[2])} />
       <Cell text={valueFormatted(feedingTrayControl[3])} colorScheme={colorScheme} textColor={textColor(feedingTrayControl[3])} />
       <Cell text={valueFormatted(feedingTrayControl[4])} colorScheme={colorScheme} textColor={textColor(feedingTrayControl[4])} />
       <Cell width="64px" paddingY="0px" justifyContent="center" colorScheme={colorScheme} alignItems="center">
         {isRemarkExist ? <DetailItemModal colorScheme={colorScheme} item={item}>
           <Icon {...iconBaseStyle} as={JalaIcon} name="comment-1" size="18px" color="primary.500" />
         </DetailItemModal> : <AutoImage source={images.empty_notes} style={EMPTY_NOTES} />}
       </Cell>
       <Cell width="48px" paddingY="0px" justifyContent="center" alignItems="center" colorScheme={colorScheme}>
         {_pushing
           && <ActivityIndicator size={15} />
           || <TouchableOpacity onPress={onPressEdit}>
             <View size="32px" justifyContent="center" alignItems="center">
               <Icon as={JalaIcon} name="pencil" size="12px" color="primary.500"/>
             </View>
         </TouchableOpacity>}
       </Cell>
       {!synced && (
         <Cell
           width="10px" right="0px" top="5px" position="absolute" paddingY="0px"
           justifyContent="center" colorScheme={colorScheme} backgroundColor="blue.200"
         >
           <Icon {...iconBaseStyle} as={JalaIcon} name="cloud-upload" size="10px" color="primary.500" />
         </Cell>
       )}
     </View>
  </LazyRender>;
})

export const CycleFeedingTrayControlListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function FeedingTrayTableScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const {
    appStore,
    appStore: {
      isOffline,
      farmStore: {
        pondStore: {
          cycleStore,
          cycleStore: {
            cycle,
            feedingTrayControlsPerDatePerTime: models,
            loadingFeedingTrayControl: loadingModel
          }
        }
      }
    },
    feedingTrayControlStore: modelStore,
    feedingTrayControlStore: { pushIdQueue, needRefresh, storing, updating, deleting, modifying },
  } = useStores()

  const [dateTimeFilter, setDateTimeFilter] = useState<{
    date_after?: string,
    date_before?: string,
    time?: string
  }>({})

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: pond - view tabs - feeding tray');
  }, []))

  useEffect(() => {
    modelStore.init();
    cycleStore.getFeedingTrayControls(!isOffline).catch();
  }, [cycleStore, isOffline, modelStore])

  useEffect(() => {
    if (needRefresh) {
      if (isOffline) {
        cycleStore.getFeedingTrayControls(false).then(() => {
          modelStore.set('needRefresh', null)
          modelStore.set('pushIdQueue', {})
        })
      } else {
        cycleStore.getFeedingTrayControls(false).then(() => {
          cycleStore.getFeedingTrayControls(true, () => {
            modelStore.set('needRefresh', null)
            modelStore.set('pushIdQueue', {})
          }).catch()
        })
      }
    }
  }, [cycleStore, isOffline, modelStore, needRefresh])

  const modelSections = useMemo(() => {
    return models.filter(({ feed_at }) => (
      (!dateTimeFilter.date_after || dateTimeFilter.date_after <= feed_at)
      && (!dateTimeFilter.date_before || feed_at <= dateTimeFilter.date_before)
    )).map(({ feed_at, age, feeding_tray_controls }) => ({
      title: { feed_at, age },
      data: feeding_tray_controls.map((m) => ({
        ...m,
        _pushing: !!m.ids.find((id) => !!pushIdQueue[id]),
        _loading: loadingModel
      }))
    }))
  }, [dateTimeFilter, loadingModel, models, pushIdQueue])

  const onAddPress = useCallback(async () => {
    JalaAnalytic.log('Mobile app: pond - add feeding tray');
    await delay(1);
    cycle && navigate(
      'Home.Stack',
      'Home.FeedingTrayControl.CreateMultiple',
      {
        cycle_id: cycle.id,
        feeding_tray_controls: _.map(
          Array((cycle || {}).feeding_tray_count || 0),
          (v, index) => ({ tray_number: index + 1, feed_remaining_percent: null })
        )
      }
    )
  }, [cycle])

  const handleEdit = useCallback((item) => {
    if (!cycle) return;
    const { feeding_tray_controls = [] } = item
    navigate(
      'Home.Stack',
      'Home.FeedingTrayControl.EditMultiple',
      {
        ...(feeding_tray_controls[0] || {}),
        feeding_tray_controls: _.map(
          Array((cycle || {}).feeding_tray_count || 0),
          (v, index) => {
            const f = feeding_tray_controls.find((f) => f.tray_number === index + 1)
            if (f) return {
              id: f.id,
              tray_number: f.tray_number,
              feed_remaining_percent: f.feed_remaining_percent
            }
            return ({ tray_number: index + 1, feed_remaining_percent: null })
          }
        )
      }
    );
  }, [cycle])

  const renderSectionHeader = useCallback((info) => (
    <SectionHeader info={info} colorScheme={colorScheme} />
  ), [colorScheme])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} handleEdit={handleEdit} />
  }, [colorScheme, handleEdit])

  const keyExtractor = useCallback((item, index) => `${(item.ids || []).join(',') || item.feed_at || index}`, [])

  const onRefresh = useCallback(() => {
    appStore.checkNetworkConnection().then(() => {
      cycleStore.getFeedingTrayControls(!isOffline).catch()
    }).catch()
  }, [appStore, cycleStore, isOffline])

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      {!!modelSections.length && <TableHead colorScheme={colorScheme} />}
      <SectionList
        sections={modelSections}
        renderSectionHeader={renderSectionHeader}
        renderItem={renderItem}
        contentContainerStyle={CONTENT_CONTAINER}
        keyExtractor={keyExtractor}
        ListEmptyComponent={(loadingModel || modifying) && (
          <View flexGrow={1} alignItems="center" justifyContent="center" height={`${height - 214}px`}>
            <JalaLoading />
          </View>
        ) || <ListEmpty text={translate("listEmpty.text")} colorScheme={colorScheme} height={`${height - 214}px`} />}
        ListHeaderComponent={(loadingModel || storing) && !!modelSections.length && !updating && !deleting && (
          <View height="40px" alignItems="center" justifyContent="center" paddingTop="8px">
            <Text tx="fetchingData" preset="body2" colorScheme={colorScheme} color={nativeBaseTheme.colors.muted[colorScheme === 'light' ? '700' : '300']} />
          </View>
        ) || undefined}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={false}
        estimatedItemSize={40}
      />
      {!cycle.finished_at && <FloatButton
        colorScheme={colorScheme}
        dateTimeFilterValue={dateTimeFilter}
        onDateTimeFilterChange={setDateTimeFilter}
        onAddPress={onAddPress}
      />}
    </Screen>
  )
})
